import moment from 'moment';

import config from '../../config';
import { ensureTimeSlot } from '../../util/data';
import { dateFromAPIToLocalNoon } from '../../util/dates';
import { LINE_ITEM_NIGHT, TIME_SLOT_DAY } from '../../util/types';

// Moment.js day-comparison helpers
const isSameDay = (day1, day2) => moment(day1).isSame(day2, 'day');
const isInclusivelyAfterDay = (day, afterDay) => moment(day).isSameOrAfter(afterDay, 'day');
const isInclusivelyBeforeDay = (day, beforeDay) => moment(day).isSameOrBefore(beforeDay, 'day');

// Checks if time slot start time equals a day (moment)
const timeSlotEqualsDay = (timeSlot, day) => {
  if (ensureTimeSlot(timeSlot).attributes.type === TIME_SLOT_DAY) {
    const localStartDate = dateFromAPIToLocalNoon(timeSlot.attributes.start);
    return isSameDay(day, moment(localStartDate));
  }
  return false;
};

/**
 * Return a boolean indicating if given date can be found in an array
 * of time slots (start dates).
 */
const timeSlotsContain = (timeSlots, date) => {
  return timeSlots.findIndex((slot) => timeSlotEqualsDay(slot, date)) > -1;
};

/**
 * Find first blocked date between two dates.
 * If none is found, null is returned.
 *
 * @param {Array} timeSlots propTypes.timeSlot objects
 * @param {Moment} startDate start date, exclusive
 * @param {Moment} endDate end date, exclusive
 */
const firstBlockedBetween = (timeSlots, startDate, endDate) => {
  const firstDate = moment(startDate).add(1, 'days');
  if (firstDate.isSame(endDate, 'date')) {
    return null;
  }

  return timeSlotsContain(timeSlots, firstDate)
    ? firstBlockedBetween(timeSlots, firstDate, endDate)
    : firstDate;
};

/**
 * Check if a blocked date can be found between two dates.
 */
export const isBlockedBetween = (timeSlots, startDate, endDate) =>
  !!firstBlockedBetween(timeSlots, startDate, endDate);

/**
 * Find the first booked/unavailable date after a given start date.
 */
export const firstBookedAfter = (timeSlots, start) => {
  if (!start) return null;
  let current = moment(start).add(1, 'day');
  const farFuture = moment().add(config.dayCountAvailableForBooking || 365, 'days');

  while (current.isBefore(farFuture)) {
    const slot = timeSlots.find((slot) => timeSlotEqualsDay(slot, current));
    if (!slot || slot.attributes.isBooked) {
      return current;
    }
    current.add(1, 'day');
  }
  return null;
};

/**
 * Check if a specific day is booked.
 */
export const isDayBookedFn = (timeSlots, day) => {
  return timeSlots.find((timeSlot) => timeSlotEqualsDay(timeSlot, day) && timeSlot.attributes.isBooked);
};

/**
 * Combined function for MUI DateRangePicker's shouldDisableDate prop.
 * Returns a function that takes a day and position, returning true if it should be disabled.
 *
 * MUI DateRangePicker passes a second argument `position` ("start" or "end") to shouldDisableDate.
 * This allows different validation for start vs end dates:
 * - START: Can't start on blocked/booked dates
 * - END: CAN end on blocked/booked dates (checkout day)
 *
 * @see https://mui.com/x/react-date-pickers/validation/
 *
 * @param {Array} timeSlots - Array of time slot objects
 * @param {Moment|null} startDate - Currently selected start date
 * @param {number|null} bookingLimit - Maximum number of days for a booking
 * @param {number} minimumNights - Minimum number of nights required for a booking
 * @param {string} unitType - Booking unit type (LINE_ITEM_DAY or LINE_ITEM_NIGHT)
 * @returns {Function} - Function that takes (day, position) and returns boolean
 */
export const shouldDisableDateFn = (
  timeSlots,
  startDate,
  bookingLimit = null,
  minimumNights = 1,
  unitType = config.bookingUnitType,
) => {
  // Pre-compute expensive values once when function is created, not on every call
  const today = moment();
  const endOfRange = config.dayCountAvailableForBooking - 1;
  const lastBookableDate = today.clone().add(endOfRange, 'days');
  const momentStartDate = startDate ? moment(startDate) : null;
  const firstBlocked = startDate ? firstBookedAfter(timeSlots, startDate) : null;

  /**
   * @param {Date|Moment} day - The day being checked
   * @param {string} position - "start" or "end" - which date is being selected/validated
   */
  return (day, position) => {
    // If no time slots, disable all dates
    if (!timeSlots || timeSlots.length === 0) {
      return true;
    }

    const isSelectingEnd = position === 'end';

    // For END date selection with a start date already selected
    if (isSelectingEnd && momentStartDate) {

      // Day is before start date (can't end before you start)
      if (!isInclusivelyAfterDay(day, momentStartDate)) {
        return true;
      }

      // Day is same as start date (need at least 1 night for nightly bookings)
      if (unitType === LINE_ITEM_NIGHT && isSameDay(day, momentStartDate)) {
        return true;
      }

      // Enforce minimum stay for nightly bookings: end date must be at least
      // `minimumNights` after the start date.
      if (unitType === LINE_ITEM_NIGHT && minimumNights > 1) {
        const minEndDate = momentStartDate.clone().add(minimumNights, 'days');
        if (moment(day).isBefore(minEndDate, 'day')) {
          return true;
        }
      }

      // Day is after last bookable date (365 days max)
      if (!isInclusivelyBeforeDay(day, lastBookableDate)) {
        return true;
      }

      // Day is after the first blocked/booked day (can't skip over blocked dates)
      // Note: you CAN end on the blocked day itself, but not after
      if (firstBlocked && !isInclusivelyBeforeDay(day, firstBlocked)) {
        return true;
      }

      // Day is after booking limit
      if (bookingLimit && !isInclusivelyBeforeDay(day, momentStartDate.clone().add(bookingLimit, 'days'))) {
        return true;
      }

      // End date is valid
      return false;
    }

    // For START date selection (or end date without start)
    // Check if day is booked - can't START on a booked day
    const booked = !!isDayBookedFn(timeSlots, day);
    if (booked) {
      return true;
    }

    // Check if day has no available timeslot
    const hasAvailableSlot = timeSlots.find(
      (timeSlot) => timeSlotEqualsDay(timeSlot, day) && !timeSlot.attributes.isBooked,
    );
    if (!hasAvailableSlot) {
      return true;
    }

    // Check basic range (today to 365 days)
    if (!isInclusivelyAfterDay(day, today)) {
      return true;
    }

    return !isInclusivelyBeforeDay(day, lastBookableDate);
  };
};
